import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ImageBackground,
  Image,
  ImageSourcePropType,
} from 'react-native';
import PlatformIcon from '../common/PlatformIcon';

// 直接定义颜色，避免 @cubemeet/core 导入问题
const MODULE_COLOR = '#F97316'; // travel 模块的橙色

export interface SimpleTravelCardMember {
  user_id: string;
  profile?: {
    display_name?: string | null;
    avatar_url?: string | null;
  } | null;
}

export interface SimpleTravelCardProps {
  id: string;
  name: string;
  location?: string | null;
  backgroundImageSource: ImageSourcePropType;
  status: string;
  members?: SimpleTravelCardMember[];
  startDate?: string | null;
  endDate?: string | null;
  budgetPerPerson?: number | null;
  currency?: string;
  onPress?: () => void;
  onChatPress?: () => void;
  getAvatarSource?: (url: string) => ImageSourcePropType | null;
}

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};

const SimpleTravelCard: React.FC<SimpleTravelCardProps> = ({
  name,
  location,
  backgroundImageSource,
  status,
  members = [],
  startDate,
  endDate,
  budgetPerPerson,
  currency = 'CNY',
  onPress,
  onChatPress,
  getAvatarSource,
}) => {
  const renderAvatar = (member: SimpleTravelCardMember, idx: number) => {
    const avatarUrl = member.profile?.avatar_url;
    const displayName = member.profile?.display_name || 'U';
    const initial = displayName.charAt(0).toUpperCase();
    const marginLeft = idx > 0 ? -8 : 0;

    if (avatarUrl && getAvatarSource) {
      const source = getAvatarSource(avatarUrl);
      if (source) {
        return (
          <Image
            key={member.user_id || idx}
            source={source}
            style={[styles.avatar, { marginLeft }]}
          />
        );
      }
    }

    return (
      <View key={member.user_id || idx} style={[styles.avatar, styles.avatarPlaceholder, { marginLeft }]}>
        <Text style={styles.avatarPlaceholderText}>{initial}</Text>
      </View>
    );
  };

  const CardContent = (
    <>
      <ImageBackground
        source={backgroundImageSource}
        style={styles.cardImage}
        imageStyle={styles.cardImageStyle}
      >
        <View style={styles.cardOverlay}>
          <View style={styles.cardTopContent}>
            <Text style={styles.cardTitle} numberOfLines={1}>{name}</Text>
          </View>
          <View style={styles.cardBottomRow}>
            {location ? (
              <View style={styles.locationBadge}>
                <PlatformIcon name="place" size={14} color="#FFFFFF" family="MaterialIcons" />
                <Text style={styles.locationText}>{location}</Text>
              </View>
            ) : <View />}
            <View style={styles.statusBadge}>
              <Text style={styles.statusText}>{status}</Text>
            </View>
          </View>
        </View>
      </ImageBackground>
      <View style={styles.cardInfo}>
        <View style={styles.membersRow}>
          <View style={styles.avatarStack}>
            {members.slice(0, 4).map((member, idx) => renderAvatar(member, idx))}
            {members.length > 4 && (
              <View style={[styles.avatar, styles.avatarMore, { marginLeft: -8 }]}>
                <Text style={styles.avatarMoreText}>+{members.length - 4}</Text>
              </View>
            )}
          </View>
          <Text style={styles.memberCount}>{members.length || 0} 名拼客</Text>
          {onChatPress && (
            <TouchableOpacity style={styles.chatButton} onPress={onChatPress}>
              <PlatformIcon name="forum" size={16} color="#fff" family="MaterialIcons" />
              <Text style={styles.chatButtonText}>进入聊天</Text>
            </TouchableOpacity>
          )}
        </View>
        {(startDate || endDate) && (
          <View style={styles.infoRow}>
            <PlatformIcon name="event" size={16} color="#6B7280" family="MaterialIcons" />
            <Text style={styles.infoText}>
              {`${formatDate(startDate)} - ${formatDate(endDate)}`}
            </Text>
          </View>
        )}
        {budgetPerPerson ? (
          <View style={styles.infoRow}>
            <PlatformIcon name="account-balance-wallet" size={16} color="#6B7280" family="MaterialIcons" />
            <Text style={styles.infoText}>{`${currency}${budgetPerPerson}/人`}</Text>
          </View>
        ) : null}
      </View>
    </>
  );

  if (onPress) {
    return (
      <TouchableOpacity style={styles.card} onPress={onPress} activeOpacity={0.9}>
        {CardContent}
      </TouchableOpacity>
    );
  }

  return <View style={styles.card}>{CardContent}</View>;
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    marginBottom: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  cardImage: {
    height: 180,
  },
  cardImageStyle: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  cardOverlay: {
    flex: 1,
    justifyContent: 'space-between',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  cardTopContent: {
    padding: 16,
    backgroundColor: 'rgba(0,0,0,0.25)',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  cardBottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  locationBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.4)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  locationText: {
    color: '#FFFFFF',
    fontSize: 12,
    marginLeft: 4,
  },
  statusBadge: {
    backgroundColor: 'rgba(0,0,0,0.5)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  cardInfo: {
    padding: 12,
  },
  membersRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  avatarStack: {
    flexDirection: 'row',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  avatarPlaceholder: {
    backgroundColor: MODULE_COLOR,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarPlaceholderText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  avatarMore: {
    backgroundColor: '#9CA3AF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarMoreText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '600',
  },
  memberCount: {
    marginLeft: 8,
    fontSize: 13,
    color: '#6B7280',
    flex: 1,
  },
  chatButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: MODULE_COLOR,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
  },
  chatButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
    marginLeft: 4,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  infoText: {
    marginLeft: 6,
    fontSize: 13,
    color: '#6B7280',
  },
});

export default SimpleTravelCard;
